import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form.tsx";
import { Input } from "@/components/ui/input.tsx";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select.tsx";
import { Textarea } from "@/components/ui/textarea";
import { SupervisorToggle } from "@/components/users/user-details/supervisor-toggle";
import { UserDetailsCard } from "@/components/users/user-details/user-details-card.tsx";
import { UserStatusToggle } from "@/components/users/user-details/user-status-toggle";
import { MultiSelect, Option } from "@/components/ui/multi-select";
import { useGroups } from "@/lib/api/group";
import { useAbility } from "@/lib/casl/ability-context";
import { hasPrivilegedAccessFromAbility } from "@/lib/casl/privileged-access";
import { isMenteeIdentity, isMentorIdentity } from "@/lib/utils/system-roles";
import {
  User,
  UserRole,
  useAssignGroups,
} from "@/lib/api/user.ts";
import { useUpdateUser } from "@/lib/hooks/user/use-update-user.ts";
import { useToast } from "@/components/ui/use-toast";
import { REDACTED_LABEL } from "@/lib/redacted-display";

interface UserExtraDetailsProps {
  user: User;
}

export function UserExtraDetails({ user }: UserExtraDetailsProps) {
  const ability = useAbility();
  const hasPrivilegedAccess = hasPrivilegedAccessFromAbility(ability);
  const canUpdateUsers = hasPrivilegedAccess && ability.can("update", "users");
  const canUpdateGroups =
    hasPrivilegedAccess && ability.can("update", "groups");
  const locationRedacted = user.redacted_fields?.includes("location") ?? false;
  const { data: groups } = useGroups();
  const assignGroups = useAssignGroups();
  const { toast } = useToast();
  const { handleUpdate, form, fieldState } = useUpdateUser({
    userId: user.id,
    location: user.location || "",
    deactivated: user.deactivated,
    email: user.email || "",
    notes: user.metadata?.notes || "",
    role: isMentorIdentity(user.systemRoles)
      ? (user.role as UserRole.MENTOR | UserRole.ADMIN)
      : undefined,
    is_supervisor: user.is_supervisor,

    profile: {
      metadata: {
        zipcode: user.profile?.metadata?.zipcode,
        insuranceProvider: user.profile?.metadata?.insuranceProvider,
      },
    },
  });

  const handleGroupChange = async (selectedOptions: Option[]) => {
    try {
      await assignGroups.mutateAsync({
        userId: user.id,
        groupIds: selectedOptions.map((option) => option.value),
      });
    } catch (error: any) {
      console.error("Error assigning groups:", error);
      const errorMessage =
        error?.response?.data?.message || "Failed to assign groups";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  const groupOptions =
    groups?.map((group) => ({
      value: group.id,
      label: group.name,
    })) || [];

  const defaultGroupValues =
    user.assigned_groups?.map((group) => ({
      value: group.id,
      label: group.name,
    })) || [];

  return (
    <UserDetailsCard>
      <Form {...form}>
        <form className="flex flex-col gap-4">
          <FormField
            disabled={!canUpdateUsers || fieldState.location || locationRedacted}
            control={form.control}
            name="location"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-neutral-500">Location</FormLabel>
                <FormControl>
                  <Input
                    placeholder={
                      locationRedacted ? REDACTED_LABEL : "Enter location"
                    }
                    {...field}
                    value={locationRedacted ? "" : (field.value ?? "")}
                    onBlur={() => {
                      if (!canUpdateUsers || locationRedacted) return;
                      handleUpdate("location", field.value);
                    }}
                  />
                </FormControl>
                <FormMessage className="text-xs" />
              </FormItem>
            )}
          />

          <FormField
            disabled={!canUpdateUsers}
            control={form.control}
            name="profile.metadata.zipcode"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-neutral-500">Zipcode</FormLabel>
                <FormControl>
                  <Input
                    placeholder="Enter zipcode"
                    {...field}
                    onBlur={() => {
                      if (!canUpdateUsers) return;
                      const currentMetadata =
                        form.getValues("profile.metadata");
                      handleUpdate("profile", {
                        metadata: {
                          ...currentMetadata,
                          zipcode: field.value,
                        },
                      });
                    }}
                  />
                </FormControl>
                <FormMessage className="text-xs" />
              </FormItem>
            )}
          />
          {isMenteeIdentity(user.systemRoles) && (
            <FormField
              control={form.control}
              name="profile.metadata.insuranceProvider"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-neutral-500">
                    Insurance Provider
                  </FormLabel>
                  <Select
                    onValueChange={(value) => {
                      if (!canUpdateUsers) return;
                      const currentMetadata =
                        form.getValues("profile.metadata");
                      handleUpdate("profile", {
                        metadata: {
                          ...currentMetadata,
                          insuranceProvider: value,
                        },
                      });
                    }}
                    defaultValue={field.value}
                    disabled={!canUpdateUsers}
                  >
                    <FormControl>
                      <SelectTrigger className="rounded-xl capitalize">
                        <SelectValue placeholder="Select insurance provider" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectGroup>
                        {[
                          "Alliance Health",
                          "Vaya Health",
                          "Trillium",
                          "Partners Health Management",
                          "United HealthCare",
                          "Blue Cross Blue Shield",
                          "Carolina Complete",
                          "Wellcare",
                          "Amerihealth Caritas",
                          "Oscar",
                          "Cigna",
                          "Humana",
                          "Ambetter",
                          "Aetna",
                          "MedCost",
                          "Other",
                          "I'm not sure",
                        ].map((provider) => (
                          <SelectItem
                            key={provider}
                            value={provider}
                            className="capitalize"
                          >
                            {provider}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />
          )}

          <div className="space-y-2">
            <label className="text-sm font-medium text-neutral-500">
              Groups
            </label>
            <MultiSelect
              options={groupOptions}
              defaultValue={defaultGroupValues}
              onChange={(selectedOptions) => {
                if (!canUpdateGroups) return;
                handleGroupChange(selectedOptions);
              }}
              className="w-full"
              placeholder="Select groups..."
              disabled={!canUpdateGroups}
            />
          </div>

          {!isMenteeIdentity(user.systemRoles) && (
            <FormField
              control={form.control}
              name="email"
              disabled
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-neutral-500">Email</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter email"
                      inputMode="email"
                      {...field}
                      onBlur={() => {
                        handleUpdate("email", field.value);
                      }}
                    />
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />
          )}

          {isMenteeIdentity(user.systemRoles) && (
            <FormField
              control={form.control}
              name="notes"
              disabled={!canUpdateUsers || fieldState.notes}
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-neutral-500">Notes</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Enter notes"
                      {...field}
                      onBlur={() => {
                        if (!canUpdateUsers) return;
                        handleUpdate("notes", field.value);
                      }}
                      className="h-24 resize-none"
                    />
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />
          )}

          {isMentorIdentity(user.systemRoles) ? (
            <>
              <FormField
                control={form.control}
                name="role"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-neutral-500">Role</FormLabel>
                    <Select
                      onValueChange={(value) =>
                        handleUpdate("role", value as UserRole)
                      }
                      defaultValue={field.value}
                      disabled={!canUpdateUsers}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select role" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectGroup>
                          <SelectItem value={UserRole.ADMIN}>Admin</SelectItem>
                          <SelectItem value={UserRole.MENTOR}>
                            Mentor
                          </SelectItem>
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                    <FormMessage className="text-xs" />
                  </FormItem>
                )}
              />
            </>
          ) : null}

          {/* TODO(SOM-3125): Remove supervisor toggle once is_supervisor is fully deprecated */}
          {user.role === UserRole.ADMIN && (
            <SupervisorToggle
              form={form}
              handleUpdate={handleUpdate}
              userId={user.id}
              disabled={!canUpdateUsers}
            />
          )}

          <UserStatusToggle
            user={user}
            onStatusChange={(deactivated) =>
              handleUpdate("deactivated", deactivated)
            }
            isLoading={fieldState.deactivated || false}
            disabled={!canUpdateUsers}
          />
        </form>
      </Form>
    </UserDetailsCard>
  );
}
